import React, { useCallback, useState, useRef, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, MapPinIcon, SendIcon, XIcon, DownloadIcon, ShareIcon } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { CheckoutData } from '../types/types';
import { CONFIG } from '../data/config';
import { OrderTicket } from './OrderTicket';
import { LocationPicker } from './LocationPicker';
import html2canvas from 'html2canvas';
interface CheckoutProps {
  onBack: () => void;
}
export function Checkout({
  onBack
}: CheckoutProps) {
  const {
    items,
    cartTotal
  } = useCart();
  const [data, setData] = useState<CheckoutData>({
    nombre: '',
    telefono: '',
    comentarios: '',
    alergias: '',
    entrega: 'Retiro en local',
    direccion: '',
    ubicacion: '',
    metodoPago: 'SINPE',
    montoEfectivo: ''
  });
  const [showTicket, setShowTicket] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [ticketImage, setTicketImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const ticketRef = useRef<HTMLDivElement>(null);
  const totalFinal = cartTotal + (data.entrega === 'Express' ? CONFIG.EXPRESS_FEE : 0);
  // Validation: if Express, they MUST provide either text address OR map location
  const isValid = data.nombre.trim() !== '' && data.telefono.trim() !== '' && items.length > 0 && (data.entrega === 'Retiro en local' || data.direccion.trim() !== '' || data.ubicacion !== '');
  const generateOrderNumber = () => {
    const now = new Date();
    const datePart = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const timePart = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
    const random = Math.floor(Math.random() * 100).toString().padStart(2, '0');
    return `AV-${datePart}-${timePart}${random}`;
  };
  const generateTicketImage = useCallback(async () => {
    if (!ticketRef.current) return null;
    try {
      const canvas = await html2canvas(ticketRef.current, {
        scale: 2,
        backgroundColor: '#FFFFFF',
        useCORS: true,
        allowTaint: true,
        logging: false
      });
      return canvas.toDataURL('image/png');
    } catch (err) {
      console.error('Error generating ticket image:', err);
      return null;
    }
  }, []);
  const handleSendOrder = async () => {
    if (!isValid) return;
    setIsGenerating(true);
    const newOrderNumber = generateOrderNumber();
    setOrderNumber(newOrderNumber);
    setShowTicket(true);
    // Wait for ticket to render
    await new Promise(resolve => setTimeout(resolve, 300));
    const imageData = await generateTicketImage();
    setTicketImage(imageData);
    setIsGenerating(false);
  };
  const generateFullTextMessage = () => {
    let msg = `Hola, quiero realizar este pedido en *${CONFIG.APP_NAME}* 🌿\n\n`;
    msg += `*PEDIDO #${orderNumber}*\n\n`;
    msg += `*DATOS DEL CLIENTE*\n`;
    msg += `Nombre: ${data.nombre}\n`;
    msg += `Teléfono: ${data.telefono}\n\n`;
    msg += `*TIPO DE ENTREGA*\n`;
    msg += `Entrega: ${data.entrega}\n`;
    if (data.entrega === 'Express') {
      if (data.direccion) msg += `Dirección: ${data.direccion}\n`;
      if (data.ubicacion) {
        // Extract lat,lng from the Google Maps URL
        const coordMatch = data.ubicacion.match(/q=([-\d.]+),([-\d.]+)/);
        if (coordMatch) {
          const lat = coordMatch[1];
          const lng = coordMatch[2];
          msg += `\n📍 *UBICACIÓN DEL CLIENTE*\n`;
          msg += `Google Maps: https://www.google.com/maps?q=${lat},${lng}\n`;
          msg += `Waze: https://waze.com/ul?ll=${lat},${lng}&navigate=yes\n`;
        } else {
          msg += `Ubicación: ${data.ubicacion}\n`;
        }
      }
      msg += `Cargo express: ${CONFIG.CURRENCY_SYMBOL}${CONFIG.EXPRESS_FEE}\n`;
    }
    msg += `\n`;
    msg += `*MÉTODO DE PAGO*\n`;
    msg += `Pago: ${data.metodoPago}\n`;
    if (data.metodoPago === 'Efectivo' && data.montoEfectivo) {
      msg += `Paga con: ${CONFIG.CURRENCY_SYMBOL}${data.montoEfectivo}\n`;
      const vuelto = parseInt(data.montoEfectivo) - totalFinal;
      if (vuelto > 0) msg += `Vuelto para: ${CONFIG.CURRENCY_SYMBOL}${vuelto}\n`;
    }
    msg += `\n`;
    msg += `*PEDIDO*\n`;
    items.forEach((item, index) => {
      msg += `${index + 1}. *${item.name}* (x${item.quantity})\n`;
      const s = item.selections;
      if (s.base) msg += `   - Base: ${s.base.name}\n`;
      if (s.proteina) msg += `   - Proteína: ${s.proteina.name}\n`;
      if (s.toppings?.length) msg += `   - Toppings: ${s.toppings.map(t => t.name).join(', ')}\n`;
      if (s.toppingsPremium?.length) msg += `   - Premium: ${s.toppingsPremium.map(t => t.name).join(', ')}\n`;
      if (s.aderezos?.length) msg += `   - Aderezos: ${s.aderezos.map(t => t.name).join(', ')}\n`;
      if (s.semillas?.length) msg += `   - Semillas: ${s.semillas.map(t => t.name).join(', ')}\n`;
      if (s.comentarios) msg += `   - Notas: ${s.comentarios}\n`;
      msg += `   - Subtotal: ${CONFIG.CURRENCY_SYMBOL}${(item.subtotal * item.quantity).toLocaleString()}\n\n`;
    });
    if (data.comentarios || data.alergias) {
      msg += `*COMENTARIOS GENERALES*\n`;
      if (data.alergias) msg += `Alergias/Restricciones: ${data.alergias}\n`;
      if (data.comentarios) msg += `Notas: ${data.comentarios}\n`;
      msg += `\n`;
    }
    msg += `*TOTAL A PAGAR*\n`;
    msg += `Subtotal: ${CONFIG.CURRENCY_SYMBOL}${cartTotal.toLocaleString()}\n`;
    if (data.entrega === 'Express') {
      msg += `Express: ${CONFIG.CURRENCY_SYMBOL}${CONFIG.EXPRESS_FEE.toLocaleString()}\n`;
    }
    msg += `*Total final: ${CONFIG.CURRENCY_SYMBOL}${totalFinal.toLocaleString()}*\n\n`;
    msg += `Gracias 🙏`;
    return msg;
  };
  const handleShareWhatsApp = async () => {
    const fullMsg = generateFullTextMessage();
    // Download ticket image to phone
    if (ticketImage) {
      const link = document.createElement('a');
      link.download = `pedido-${orderNumber}.png`;
      link.href = ticketImage;
      link.click();
    }
    // Small delay so download starts, then open WhatsApp directly to the restaurant's number
    await new Promise(resolve => setTimeout(resolve, 400));
    const encoded = encodeURIComponent(fullMsg);
    window.open(`https://wa.me/${CONFIG.WHATSAPP_NUMBER}?text=${encoded}`, '_blank');
  };
  const handleDownloadTicket = () => {
    if (!ticketImage) return;
    const link = document.createElement('a');
    link.download = `pedido-${orderNumber}.png`;
    link.href = ticketImage;
    link.click();
  };
  return <motion.div initial={{
    opacity: 0,
    x: 50
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: 50
  }} className="fixed inset-0 z-40 bg-carbon flex flex-col max-w-md mx-auto" data-id="element-146">
      <div className="bg-surface px-4 py-4 flex items-center shadow-sm z-10 border-b border-gray-800" data-id="element-147">
        <button onClick={onBack} className="p-2 -ml-2 text-gray-400 hover:text-white" data-id="element-148">
          <ChevronLeftIcon className="w-6 h-6" data-id="element-149" />
        </button>
        <h2 className="font-heading font-semibold text-lg ml-2 text-white tracking-wide" data-id="element-150">
          Finalizar Pedido
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto p-4 pb-32 space-y-6" data-id="element-151">
        {/* Datos Personales */}
        <section className="bg-surface p-5 rounded-2xl shadow-sm border border-gray-800" data-id="element-152">
          <h3 className="font-heading font-semibold text-white mb-1 tracking-wide" data-id="element-153">
            Tus Datos
          </h3>
          <p className="text-xs text-gray-400 mb-4" data-id="element-154">
            Necesitamos estos datos para contactarte sobre tu pedido
          </p>
          <div className="space-y-3" data-id="element-155">
            <div data-id="element-156">
              <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-157">
                Nombre completo <span className="text-terracota" data-id="element-158">*</span>
              </label>
              <input type="text" value={data.nombre} onChange={e => setData({
              ...data,
              nombre: e.target.value
            })} className="w-full p-3 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none text-white placeholder-gray-500" placeholder="Ej. María Pérez" data-id="element-159" />
            </div>
            <div data-id="element-160">
              <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-161">
                Teléfono <span className="text-terracota" data-id="element-162">*</span>
              </label>
              <input type="tel" value={data.telefono} onChange={e => setData({
              ...data,
              telefono: e.target.value
            })} className="w-full p-3 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none text-white placeholder-gray-500" placeholder="Ej. 88888888" data-id="element-163" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-164">
                Para coordinación de entrega o dudas sobre tu pedido
              </p>
            </div>
          </div>
        </section>

        {/* Entrega */}
        <section className="bg-surface p-5 rounded-2xl shadow-sm border border-gray-800" data-id="element-165">
          <h3 className="font-heading font-semibold text-white mb-1 tracking-wide" data-id="element-166">
            Tipo de Entrega
          </h3>
          <p className="text-xs text-gray-400 mb-4" data-id="element-167">
            Elegí cómo querés recibir tu pedido
          </p>
          <div className="flex gap-3 mb-4" data-id="element-168">
            {['Retiro en local', 'Express'].map(tipo => <button key={tipo} onClick={() => setData({
            ...data,
            entrega: tipo as any
          })} className={`flex-1 py-3 rounded-xl text-sm font-medium border-2 transition-colors ${data.entrega === tipo ? 'border-verde bg-verde/10 text-verde' : 'border-gray-700 text-gray-400 hover:bg-surface-light'}`} data-id="element-169">
                {tipo}
              </button>)}
          </div>

          {data.entrega === 'Retiro en local' && <p className="text-xs text-gray-400 bg-surface-light p-3 rounded-lg flex items-start gap-2 border border-gray-800" data-id="element-170">
              <span className="text-base leading-none" data-id="element-171">📍</span>
              Pasá a recoger tu pedido cuando esté listo. Te avisaremos por
              WhatsApp.
            </p>}

          {data.entrega === 'Express' && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} className="space-y-3" data-id="element-172">
              <p className="text-xs text-terracota bg-terracota/10 p-3 rounded-lg flex items-start gap-2 border border-terracota/20" data-id="element-173">
                <span className="text-base leading-none" data-id="element-174">🛵</span>
                El servicio express tiene un cargo adicional de{' '}
                {CONFIG.CURRENCY_SYMBOL}
                {CONFIG.EXPRESS_FEE.toLocaleString()}
              </p>

              <div data-id="element-175">
                <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-176">
                  Dirección exacta{' '}
                  <span className="text-xs text-gray-500 font-normal" data-id="element-177">
                    (opcional si compartís ubicación)
                  </span>
                </label>
                <textarea value={data.direccion} onChange={e => setData({
              ...data,
              direccion: e.target.value
            })} className="w-full p-3 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none resize-none h-20 text-white placeholder-gray-500" placeholder="Ej: San José, Escazú, frente al parque central..." data-id="element-178" />
              </div>
              <div className="pt-2" data-id="element-179">
                <label className="block text-xs font-medium text-gray-400 mb-2" data-id="element-180">
                  Ubicación en el mapa{' '}
                  <span className="text-xs text-gray-500 font-normal" data-id="element-181">
                    (recomendado para mayor precisión)
                  </span>
                </label>
                <LocationPicker onConfirm={url => setData({
              ...data,
              ubicacion: url
            })} initialLocation={data.ubicacion} data-id="element-182" />
              </div>
            </motion.div>}
        </section>

        {/* Pago */}
        <section className="bg-surface p-5 rounded-2xl shadow-sm border border-gray-800" data-id="element-183">
          <h3 className="font-heading font-semibold text-white mb-1 tracking-wide" data-id="element-184">
            Método de Pago
          </h3>
          <p className="text-xs text-gray-400 mb-4" data-id="element-185">
            ¿Cómo vas a pagar tu pedido?
          </p>
          <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-186">
            {['SINPE', 'Tarjeta', 'Efectivo'].map(tipo => <button key={tipo} onClick={() => setData({
            ...data,
            metodoPago: tipo as any
          })} className={`py-3 rounded-xl text-sm font-medium border-2 transition-colors ${data.metodoPago === tipo ? 'border-verde bg-verde/10 text-verde' : 'border-gray-700 text-gray-400 hover:bg-surface-light'}`} data-id="element-187">
                {tipo}
              </button>)}
          </div>

          {data.metodoPago === 'SINPE' && <p className="text-xs text-gray-400 bg-surface-light p-3 rounded-lg border border-gray-800" data-id="element-188">
              📱 Te enviaremos el número de SINPE por WhatsApp para que realices
              la transferencia
            </p>}
          {data.metodoPago === 'Tarjeta' && <p className="text-xs text-gray-400 bg-surface-light p-3 rounded-lg border border-gray-800" data-id="element-189">
              💳 Coordinamos el pago con tarjeta al momento de la entrega o
              retiro
            </p>}

          {data.metodoPago === 'Efectivo' && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} data-id="element-190">
              <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-191">
                ¿Con cuánto pagás?{' '}
                <span className="text-xs text-gray-500 font-normal" data-id="element-192">
                  (opcional)
                </span>
              </label>
              <input type="number" value={data.montoEfectivo} onChange={e => setData({
            ...data,
            montoEfectivo: e.target.value
          })} className="w-full p-3 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none text-white placeholder-gray-500" placeholder="Ej. 10000 — para calcular tu vuelto" data-id="element-193" />
              {data.montoEfectivo && parseInt(data.montoEfectivo) > totalFinal && <p className="text-sm text-gray-400 mt-2" data-id="element-194">
                    Vuelto estimado:{' '}
                    <span className="font-semibold text-white" data-id="element-195">
                      {CONFIG.CURRENCY_SYMBOL}
                      {parseInt(data.montoEfectivo) - totalFinal}
                    </span>
                  </p>}
              {!data.montoEfectivo && <p className="text-xs text-gray-500 mt-1" data-id="element-196">
                  Así preparamos tu vuelto exacto
                </p>}
            </motion.div>}
        </section>

        {/* Extras */}
        <section className="bg-surface p-5 rounded-2xl shadow-sm border border-gray-800" data-id="element-197">
          <div className="flex items-center gap-2 mb-1" data-id="element-198">
            <h3 className="font-heading font-semibold text-white tracking-wide" data-id="element-199">
              Comentarios Adicionales
            </h3>
            <span className="text-xs font-medium bg-surface-light text-gray-400 px-2 py-0.5 rounded-full border border-gray-700" data-id="element-200">
              Opcional
            </span>
          </div>
          <p className="text-xs text-gray-400 mb-4" data-id="element-201">
            Si no tenés nada que agregar, podés enviar tu pedido directamente
          </p>
          <div className="space-y-3" data-id="element-202">
            <div data-id="element-203">
              <label className="block text-xs font-medium text-red-400 mb-1" data-id="element-204">
                Alergias o restricciones alimentarias
              </label>
              <input type="text" value={data.alergias} onChange={e => setData({
              ...data,
              alergias: e.target.value
            })} className="w-full p-3 bg-red-950/20 border border-red-900/50 rounded-xl focus:ring-2 focus:ring-red-500 outline-none placeholder-red-900/50 text-red-400" placeholder="Ej: Alérgico al maní, intolerante a la lactosa..." data-id="element-205" />
              <p className="text-xs text-red-500/70 mt-1" data-id="element-206">
                ⚠️ Importante si tenés alguna alergia — dejalo vacío si no
                aplica
              </p>
            </div>
            <div data-id="element-207">
              <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-208">
                Notas para el restaurante
              </label>
              <textarea value={data.comentarios} onChange={e => setData({
              ...data,
              comentarios: e.target.value
            })} className="w-full p-3 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none resize-none h-20 text-white placeholder-gray-500" placeholder="Ej: Llegar por la puerta trasera, timbre no funciona, etc." data-id="element-209" />
            </div>
          </div>
        </section>
      </div>

      {/* Footer Sticky */}
      <div className="bg-surface border-t border-gray-800 p-4 pb-safe fixed bottom-0 left-0 right-0 max-w-md mx-auto z-20" data-id="element-210">
        <div className="flex justify-between items-center mb-3 px-2" data-id="element-211">
          <span className="text-gray-400 font-medium" data-id="element-212">Total a pagar</span>
          <span className="font-bold text-2xl text-white" data-id="element-213">
            {CONFIG.CURRENCY_SYMBOL}
            {totalFinal.toLocaleString()}
          </span>
        </div>
        <button onClick={handleSendOrder} disabled={!isValid || isGenerating} className={`w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all border border-white/10 ${isValid && !isGenerating ? 'bg-[#25D366] text-white hover:bg-[#1ebd5a] shadow-lg shadow-[#25D366]/20 active:scale-95' : 'bg-gray-800 text-gray-500 cursor-not-allowed'}`} data-id="element-214">
          <SendIcon className="w-5 h-5" data-id="element-215" />
          {isGenerating ? 'Generando ticket...' : 'Enviar pedido por WhatsApp'}
        </button>
        {!isValid && <p className="text-center text-xs text-red-500 mt-2" data-id="element-216">
            {data.nombre.trim() === '' && '• Completá tu nombre '}
            {data.telefono.trim() === '' && '• Agregá tu teléfono '}
            {items.length === 0 && '• Agregá al menos un producto '}
            {data.entrega === 'Express' && data.direccion.trim() === '' && data.ubicacion === '' && '• Agregá dirección o ubicación en el mapa '}
          </p>}
      </div>

      {/* Hidden ticket for rendering */}
      <div style={{
      position: 'absolute',
      left: '-9999px',
      top: 0
    }} data-id="element-217">
        <OrderTicket ref={ticketRef} orderNumber={orderNumber} data={data} items={items} cartTotal={cartTotal} totalFinal={totalFinal} data-id="element-218" />
      </div>

      {/* Ticket Preview Modal */}
      <AnimatePresence data-id="element-219">
        {showTicket && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowTicket(false)} className="fixed inset-0 bg-black/80 z-50 backdrop-blur-sm" data-id="element-220" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 40
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 40
        }} transition={{
          type: 'spring',
          damping: 25
        }} className="fixed inset-x-4 top-8 bottom-8 z-50 flex flex-col items-center max-w-md mx-auto" data-id="element-221">
              {/* Close button */}
              <button onClick={() => setShowTicket(false)} className="absolute top-2 right-2 z-10 bg-surface rounded-full p-2 shadow-md border border-gray-700" data-id="element-222">
                <XIcon className="w-5 h-5 text-gray-300" data-id="element-223" />
              </button>

              {/* Ticket image preview */}
              <div className="flex-1 overflow-y-auto w-full flex justify-center py-4" data-id="element-224">
                {ticketImage ? <img src={ticketImage} alt="Ticket de pedido" className="w-full max-w-[340px] rounded-2xl shadow-2xl object-contain" data-id="element-225" /> : <div className="flex items-center justify-center h-full" data-id="element-226">
                    <div className="text-white text-center" data-id="element-227">
                      <div className="w-8 h-8 border-2 border-verde border-t-transparent rounded-full animate-spin mx-auto mb-3" data-id="element-228" />
                      <p className="text-sm" data-id="element-229">Generando ticket...</p>
                    </div>
                  </div>}
              </div>

              {/* Action buttons */}
              {ticketImage && <div className="w-full flex gap-3 pt-3 pb-2 px-2" data-id="element-230">
                  <button onClick={handleDownloadTicket} className="flex-1 bg-surface-light text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 shadow-md active:scale-95 transition-transform border border-gray-700" data-id="element-231">
                    <DownloadIcon className="w-5 h-5" data-id="element-232" />
                    Guardar
                  </button>
                  <button onClick={handleShareWhatsApp} className="flex-[2] bg-[#25D366] text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-[#25D366]/20 active:scale-95 transition-transform border border-white/10" data-id="element-233">
                    <SendIcon className="w-5 h-5" data-id="element-234" />
                    Enviar por WhatsApp
                  </button>
                </div>}
            </motion.div>
          </>}
      </AnimatePresence>
    </motion.div>;
}